import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money, formatApiErrorDetail, downloadCsv } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, ChevronLeft, ChevronRight, Lock, LockOpen, CheckCircle2, AlertCircle, ArrowRight, Download, Receipt, Landmark, FileWarning, TrendingUp } from "lucide-react";

const shiftMonth = (m, d) => {
  const [y, mm] = m.split("-").map(Number);
  const dt = new Date(Date.UTC(y, mm - 1 + d, 1));
  return dt.toISOString().slice(0, 7);
};
const label = (m) => new Date(`${m}-01T00:00:00Z`).toLocaleDateString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });

export default function FinanceClose() {
  const { user } = useAuth();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [s, setS] = useState(null);
  const [ageing, setAgeing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/finance/summary?month=${month}`).then((r) => setS(r.data));
    api.get("/admin/ageing").then((r) => setAgeing(r.data));
  }, [month]);
  useEffect(() => { setS(null); load(); }, [load]);

  const act = async (path, body, ok) => {
    setBusy(true);
    try {
      await api.post(path, body);
      toast.success(ok);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Finance Close"
        subtitle="One queue for the billing cycle: receipts, ageing, deductions, approvals, payouts and the owner's bottom line."
        actions={
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="finance-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="font-display font-semibold w-40 text-center" data-testid="finance-month-label">{label(month)}</span>
            <Button variant="outline" size="icon" onClick={() => setMonth(shiftMonth(month, 1))} data-testid="finance-next-month"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        }
      />
      {!s ? <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <div className="space-y-6">
          <Card className={`p-5 flex flex-wrap items-center justify-between gap-4 ${s.closed ? "border-primary/40" : "border-amber-500/40"}`} data-testid="finance-status-card">
            <div className="flex items-center gap-3">
              {s.closed ? <Lock className="w-5 h-5 text-primary" /> : <LockOpen className="w-5 h-5 text-amber-600" />}
              <div>
                <p className="font-display font-semibold">{s.closed ? "Month closed" : "Month open"}</p>
                <p className="text-xs text-muted-foreground">{s.closed ? `Closed ${s.closed_at?.slice(0, 16).replace("T", " ")} by ${s.closed_by}` : s.can_close ? "All blocking items are clear - you can close this billing cycle." : "Blocking items remain (see checklist)."}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => downloadCsv(`/admin/export/receipts?month=${month}`, `receipts-${month}.csv`)} data-testid="export-receipts-button"><Download className="w-4 h-4 mr-1" /> Receipts CSV</Button>
              <Button variant="outline" size="sm" onClick={() => downloadCsv("/admin/export/ageing", "ageing.csv")} data-testid="export-ageing-button"><Download className="w-4 h-4 mr-1" /> Ageing CSV</Button>
              {s.closed
                ? user?.role === "admin" && <Button variant="outline" size="sm" disabled={busy} onClick={() => act("/admin/months/reopen", { month }, `${month} reopened`)} data-testid="finance-reopen-button">Reopen month</Button>
                : <Button size="sm" disabled={busy || !s.can_close} onClick={() => window.confirm(`Close ${label(month)}? Invoices freeze, payouts are generated and deposit deductions are proposed for overdue invoices.`) && act("/admin/months/close", { month }, `${month} closed - payouts generated`)} data-testid="finance-close-button"><Lock className="w-4 h-4 mr-1" /> Close month</Button>}
            </div>
          </Card>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Invoiced" value={money(s.invoices.value)} sub={`${s.invoices.count} invoices · ${s.invoices.credit_notes} credit note(s)`} icon={Receipt} />
            <StatCard label="Receipts this month" value={money(s.receipts.total)} sub={Object.entries(s.receipts.by_mode).map(([k, v]) => `${k.replace("_", " ")} ${money(v)}`).join(" · ") || "no receipts posted"} icon={Landmark} />
            <StatCard label="Open balance (month)" value={money(s.invoices.open_balance)} sub={`${s.invoices.pending} unpaid · ${s.invoices.partial} partial`} icon={FileWarning} accent />
            <StatCard label="Owner net profit" value={money(s.owner.net_profit)} sub={`${s.owner.owner_pct_of_profit}% of gross profit ex-VAT`} icon={TrendingUp} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <Card className="p-5 lg:col-span-3" data-testid="finance-checklist">
              <h3 className="font-display font-semibold mb-3">Close checklist</h3>
              <div className="space-y-2">
                {s.checklist.map((c) => (
                  <Link key={c.key} to={c.link} className="flex items-center gap-3 rounded-lg border border-border/60 px-3 py-2.5 hover:bg-secondary/40 transition-colors" data-testid={`checklist-${c.key}`}>
                    {c.ok ? <CheckCircle2 className="w-5 h-5 text-primary shrink-0" /> : <AlertCircle className={`w-5 h-5 shrink-0 ${c.blocking ? "text-destructive" : "text-amber-600"}`} />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{c.label}</p>
                      <p className="text-xs text-muted-foreground truncate">{c.hint}</p>
                    </div>
                    {!c.ok && <Badge variant={c.blocking ? "destructive" : "secondary"}>{c.count}{c.blocking ? " · blocking" : ""}</Badge>}
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </Link>
                ))}
              </div>
            </Card>

            <Card className="p-5 lg:col-span-2" data-testid="finance-owner-card">
              <h3 className="font-display font-semibold mb-3">Owner bottom line · {label(month)}</h3>
              <div className="text-sm space-y-1.5">
                <Row l="Collected sales" v={money(s.totals.total_sales)} />
                <Row l="Gross profit ex-VAT" v={money(s.owner.gross_profit_ex_vat)} />
                <Row l="Less base commission" v={`- ${money(s.owner.base_commission)}`} muted />
                <Row l="Less accelerator / new-customer / star" v={`- ${money(s.owner.less_bonuses)}`} muted />
                <Row l="Plus owner share of above-list premium" v={`+ ${money(s.owner.owner_share_premium)}`} muted />
                <div className="flex justify-between font-display font-bold text-base border-t border-border pt-2"><span>Net profit</span><span>{money(s.owner.net_profit)}</span></div>
              </div>
              <div className="mt-4 text-xs text-muted-foreground">
                Payouts {s.payouts.paid}/{s.payouts.generated} paid · {money(s.payouts.total)} · due {s.payouts.due_date}{s.payouts.overdue && <Badge variant="destructive" className="ml-2">overdue</Badge>}
              </div>
            </Card>
          </div>

          <Card className="p-5" data-testid="finance-ageing">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-semibold">Receivables ageing (all open invoices)</h3>
              <span className="text-sm text-muted-foreground">Total open <b className="text-foreground">{money(s.ageing.grand_total)}</b> · overdue <b className="text-destructive">{money(s.ageing.overdue_total)}</b></span>
            </div>
            <AgeingBar totals={s.ageing.totals} buckets={s.ageing.buckets} grand={s.ageing.grand_total} />
            {ageing && ageing.by_customer.length > 0 && (
              <div className="overflow-x-auto mt-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                      <th className="text-left font-medium px-3 py-2">Customer</th>
                      {ageing.buckets.map((b) => <th key={b} className="text-right font-medium px-3 py-2">{b === "current" ? "Not due" : `${b} d`}</th>)}
                      <th className="text-right font-medium px-3 py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ageing.by_customer.map((r) => (
                      <tr key={r.customer} className="border-b border-border/40" data-testid={`ageing-row-${r.customer}`}>
                        <td className="px-3 py-2 font-medium">{r.customer} <span className="text-xs text-muted-foreground">· {r.invoices} inv</span></td>
                        {ageing.buckets.map((b) => <td key={b} className={`px-3 py-2 text-right ${r[b] > 0 && b !== "current" ? "text-destructive" : "text-muted-foreground"}`}>{r[b] ? money(r[b]) : "—"}</td>)}
                        <td className="px-3 py-2 text-right font-medium">{money(r.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

function Row({ l, v, muted }) {
  return <div className={`flex justify-between ${muted ? "text-muted-foreground" : ""}`}><span>{l}</span><span>{v}</span></div>;
}

const COLORS = { current: "bg-primary/60", "1-30": "bg-amber-400", "31-60": "bg-orange-500", "61-90": "bg-red-500", "90+": "bg-red-800" };
function AgeingBar({ totals, buckets, grand }) {
  if (!grand) return <p className="text-sm text-muted-foreground">No open receivables.</p>;
  return (
    <div>
      <div className="flex h-3 rounded-full overflow-hidden bg-secondary">
        {buckets.map((b) => totals[b] > 0 && <div key={b} className={COLORS[b]} style={{ width: `${(totals[b] / grand) * 100}%` }} title={`${b}: ${money(totals[b])}`} />)}
      </div>
      <div className="flex flex-wrap gap-4 mt-2 text-xs">
        {buckets.map((b) => <span key={b} className="flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-sm ${COLORS[b]}`} />{b === "current" ? "Not yet due" : `${b} days`} <b>{money(totals[b])}</b></span>)}
      </div>
    </div>
  );
}
